/**
 * ContractorOfferArchive — contractor-facing read-only archive of past offers.
 *
 * Shows accepted/declined/expired/withdrawn/superseded offers for the
 * currently-resolved contractor. Displays payoutAmountSnapshot ONLY.
 * MUST NOT render the customer revenue field, internal pricing fields, margin, or any internal
 * pricing field. Enforced by the canonicalSyncGuardrail tests.
 */

import { useMemo } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useCurrentContractor } from "@/hooks/useCurrentContractor";
import { getContractorOfferHistory } from "@/data/contractor/offerMetrics";
import { OFFER_DECLINE_REASONS } from "@/data/contractor/marketplaceOffer";
import { OFFER_HISTORY_EMPTY } from "@/data/contractor/contractorMessaging";
import { Card, CardContent } from "@/components/ui/card";
import { Inbox } from "lucide-react";

const DECLINE_LABELS: Record<string, string> = Object.fromEntries(
  OFFER_DECLINE_REASONS.map((r) => [r.value, r.label]),
);

const STATUS_TONE: Record<string, string> = {
  accepted: "text-success",
  declined: "text-destructive",
  expired: "text-muted-foreground",
  withdrawn: "text-muted-foreground",
  superseded: "text-muted-foreground",
  pending: "text-warning",
};

export function ContractorOfferArchive() {
  const { marketplaceOffers } = useAppData();
  const { id } = useCurrentContractor();

  const history = useMemo(
    () => getContractorOfferHistory(id, marketplaceOffers),
    [id, marketplaceOffers],
  );

  return (
    <Card>
      <CardContent className="p-4">
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-2 mb-3">
          <Inbox className="h-4 w-4" /> My Offer Archive
        </h2>

        {history.length === 0 ? (
          <p className="text-xs text-muted-foreground">{OFFER_HISTORY_EMPTY}</p>
        ) : (
          <ul className="divide-y divide-border/60">
            {history.map((o) => (
              <li key={o.id} className="py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[11px] text-muted-foreground">{o.jobId}</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider ${STATUS_TONE[o.status]}`}>
                    {o.status}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1 text-[11px]">
                  <span className="text-muted-foreground">
                    Sent {new Date(o.sentAt).toLocaleDateString()}
                    {o.respondedAt ? ` · Responded ${new Date(o.respondedAt).toLocaleDateString()}` : ""}
                  </span>
                  {o.payoutAmountSnapshot != null && (
                    <span className="font-semibold tabular-nums">
                      ${o.payoutAmountSnapshot.toLocaleString()}
                    </span>
                  )}
                </div>
                {o.responseReason && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Reason: {DECLINE_LABELS[o.responseReason] ?? o.responseReason}
                    {o.responseNote ? ` — ${o.responseNote}` : ""}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
